import { useState, useEffect } from 'react';
import { Tooltip } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { PLACEHOLDER_IMAGE } from '../utils';
import * as styles from '../styles';
import { inspectionClient } from '@/config/api';

// Mapeamento de fonte para logo
const LOGO_MAP: Record<string, string> = {
  chavesnamao: '/assets/chavesnamao.png',
  vivareal: '/assets/vivareal.png',
  zap: '/assets/zap.png',
  zapimoveis: '/assets/zap.png', // Bot retorna 'zapimoveis'
  imovelweb: '/assets/imovelweb.png',
};

// Cache em memória para evitar re-fetch de imagens já carregadas
const imageCache = new Map<string, string>();

function useScrapedImage(externalId?: string, imagemUrl?: string) {
  const [src, setSrc] = useState<string | undefined>(() => {
    if (imagemUrl?.startsWith('data:image/')) return imagemUrl;
    if (externalId && imageCache.has(externalId)) return imageCache.get(externalId);
    return undefined;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!externalId || src) return;

    let cancelled = false;
    setLoading(true);

    inspectionClient.get(
      `/api/scraped-properties/${externalId}/image`,
      { responseType: 'blob' }
    ).then(response => {
      if (cancelled) return;
      const blob = response.data as Blob;
      const reader = new FileReader();
      reader.onloadend = () => {
        if (cancelled) return;
        const base64 = reader.result as string;
        imageCache.set(externalId, base64);
        setSrc(base64);
        setLoading(false);
      };
      reader.readAsDataURL(blob);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [externalId, src]);

  return { src, loading };
}

interface AmostraImageProps {
  fonte?: string | undefined;
  identificacao: string;
  urlOriginal?: string | undefined;
  fotoFachada?: string | undefined;
  imagemUrl?: string | undefined;
  externalId?: string | undefined;
}

export function AmostraImage({ fonte, identificacao, urlOriginal, fotoFachada, imagemUrl, externalId }: AmostraImageProps) {
  const logoSrc = fonte ? LOGO_MAP[fonte.toLowerCase()] : null;
  const isAvaliando = identificacao === 'Avaliando';
  const { src: scrapedSrc } = useScrapedImage(externalId, imagemUrl);

  // Para o card "Avaliando", mostra a foto de fachada se disponível
  if (isAvaliando && fotoFachada) {
    return (
      <div style={styles.imageContainerStyle}>
        <img
          src={fotoFachada}
          alt="Fachada do imóvel"
          width={96}
          height={96}
          style={{ ...styles.imageStyle, objectFit: 'cover' }}
          onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
        />
      </div>
    );
  }

  // Se tem imagem (base64 direto ou carregada via API)
  const imgSrc = imagemUrl?.startsWith('data:image/') ? imagemUrl : scrapedSrc;
  if (imgSrc) {
    const imgElement = (
      <img
        src={imgSrc}
        alt="Foto do imóvel"
        width={96}
        height={96}
        style={{ ...styles.imageStyle, objectFit: 'cover', cursor: urlOriginal ? 'pointer' : 'default' }}
        onError={(e) => {
          const logo = fonte ? LOGO_MAP[fonte.toLowerCase()] : null;
          e.currentTarget.src = logo || PLACEHOLDER_IMAGE;
          e.currentTarget.style.objectFit = 'contain';
          e.currentTarget.style.padding = '8px';
          e.currentTarget.style.backgroundColor = '#f5f5f5';
        }}
      />
    );

    if (urlOriginal) {
      return (
        <Tooltip title={`Abrir anúncio no ${fonte || 'site'}`}>
          <a href={urlOriginal} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
            <div style={styles.imageContainerStyle}>
              {imgElement}
            </div>
          </a>
        </Tooltip>
      );
    }

    return (
      <div style={styles.imageContainerStyle}>
        {imgElement}
      </div>
    );
  }

  // Se tem fonte e logo, mostra o logo clicável
  if (logoSrc) {
    const imgElement = (
      <img
        src={logoSrc}
        alt={fonte}
        width={96}
        height={96}
        style={{ ...styles.imageStyle, objectFit: 'contain', padding: 8, backgroundColor: '#f5f5f5', cursor: urlOriginal ? 'pointer' : 'default' }}
        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
      />
    );

    if (urlOriginal) {
      return (
        <Tooltip title={`Abrir anúncio no ${fonte}`}>
          <a href={urlOriginal} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
            <div style={styles.imageContainerStyle}>
              {imgElement}
            </div>
          </a>
        </Tooltip>
      );
    }

    return (
      <div style={styles.imageContainerStyle}>
        {imgElement}
      </div>
    );
  }

  // Fallback: ícone de casa para amostras manuais ou sem fonte
  return (
    <div style={styles.imageContainerStyle}>
      <div
        style={{
          width: 96,
          height: 96,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          borderRadius: 8,
        }}
      >
        <HomeOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
      </div>
    </div>
  );
}
